import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type PropsWithChildren,
} from 'react';

import type { Panel } from '../types';
import { useCurrentPreviewPanel } from '../use-current-preview-panel';
import { usePreviewPanelsActions } from '../use-preview-panel-actions';

interface ProviderContext {
	canNavigateBack: boolean;
	history: Panel[];
	navigateBack: () => void;
	onPanelOpen: () => void;
}

interface ProviderProps {
	/** Setter for the history data. Called whenever the navigation history changes */
	setHistory?: (history: Panel[]) => void;
}

const noop = () => {};

const PreviewPanelsNavigationContext = createContext<ProviderContext>({
	history: [],
	canNavigateBack: false,
	onPanelOpen: noop,
	navigateBack: noop,
});

export const PreviewPanelsNavigationProvider = ({
	children,
	setHistory,
}: PropsWithChildren<ProviderProps>): React.JSX.Element => {
	const [panelHistory, setPanelHistory] = useState<Panel[]>([]);

	/**
	 * Tracks the panel we're navigating away from during `navigateBack` so the next `onPanelOpen` triggered by re-opening the popped panel can be ignored.
	 * A ref is required because `onPanelOpen` can be called synchronously within the same tick (`PreviewPanelsProvider` calls the `onOpen` callback before committing/re-rendering).
	 */
	const navigateBackFromPanelRef = useRef<Panel | null>(null);

	const currentPanel = useCurrentPreviewPanel();
	const { open: openPreviewPanel } = usePreviewPanelsActions();

	const addHistoryItem = useCallback(
		(panel: Panel) => {
			const updatedPanelHistory = [...panelHistory, panel];
			setPanelHistory(updatedPanelHistory);
			setHistory?.(updatedPanelHistory);
		},
		[panelHistory, setHistory],
	);

	const removeLastHistoryItem = useCallback(() => {
		const previousItem = panelHistory.at(-1);

		if (previousItem == null) return null;

		const updatedPanelHistory = panelHistory.slice(0, -1);
		setPanelHistory(updatedPanelHistory);
		setHistory?.(updatedPanelHistory);

		return previousItem;
	}, [panelHistory, setHistory]);

	const clearHistory = useCallback(() => {
		setPanelHistory([]);
		setHistory?.([]);
	}, [setHistory]);

	const onPanelOpen = useCallback(() => {
		if (currentPanel != null && navigateBackFromPanelRef.current?.id !== currentPanel.id) {
			addHistoryItem(currentPanel);
		}

		navigateBackFromPanelRef.current = null;
	}, [currentPanel, addHistoryItem]);

	const navigateBack = useCallback(() => {
		if (currentPanel == null) return;

		const panelToOpen = removeLastHistoryItem();

		if (panelToOpen == null) return;

		navigateBackFromPanelRef.current = currentPanel;

		const open = () => openPreviewPanel({ panel: panelToOpen });

		if (panelToOpen.onNavigateBack != null) {
			panelToOpen.onNavigateBack(currentPanel, open);
		} else {
			open();
		}
	}, [currentPanel, openPreviewPanel, removeLastHistoryItem]);

	const canNavigateBack = panelHistory.length > 0;

	useEffect(() => {
		// Ensure history is empty when preview panel is closed
		if (canNavigateBack && currentPanel == null) {
			clearHistory();
			navigateBackFromPanelRef.current = null;
		}
	}, [canNavigateBack, currentPanel, clearHistory]);

	const context = useMemo(
		() => ({
			history: panelHistory,
			canNavigateBack,
			onPanelOpen,
			navigateBack,
		}),
		[canNavigateBack, panelHistory, onPanelOpen, navigateBack],
	);

	return (
		<PreviewPanelsNavigationContext.Provider value={context}>
			{children}
		</PreviewPanelsNavigationContext.Provider>
	);
};

export const usePreviewPanelsNavigation = (): ProviderContext =>
	useContext(PreviewPanelsNavigationContext);
